import React, { useMemo } from "react";

import { ClientWithAdvisorName } from "@src/multiCustodian/components/Advisor/Clients/types";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

interface ColumnDefinition<T> {
  loading?: Cell;
  empty?: Cell;
  displayData: (data: T, ...flags: boolean[]) => Cell | string;
}

type ColumnDefinitions<K extends string, T = unknown> = Record<
  K,
  ColumnDefinition<T>
>;

// Specific type exports for each table
export type ClientColumnDefinitions<K extends string> = ColumnDefinitions<
  K,
  ClientWithAdvisorName
>;

interface UseTableRowsConfig<K extends string, T = unknown> {
  searchedData: Row<K, Cell>[];
  columnDefinitions: ColumnDefinitions<K, T>;
  isLoading: boolean;
  loadingRowCount?: number;
}

function createRow<K extends string, T = unknown>(
  columnDefinitions: ColumnDefinitions<K, T>,
  rowType: "loading" | "empty",
  defaultValue: Cell
): Row<K, Cell> {
  const result: Record<string, Cell | string> = { key: rowType };
  Object.keys(columnDefinitions).forEach((key) => {
    const columnKey = key as K;
    const columnDef = columnDefinitions[columnKey];
    result[columnKey] = columnDef[rowType] || defaultValue;
  });

  return result as Row<K, Cell>;
}

function createLoadingRow<K extends string, T = unknown>(
  columnDefinitions: ColumnDefinitions<K, T>
): Row<K, Cell> {
  return createRow(columnDefinitions, "loading", { value: <Skeleton /> });
}

function createEmptyRow<K extends string, T = unknown>(
  columnDefinitions: ColumnDefinitions<K, T>
): Row<K, Cell> {
  return createRow(columnDefinitions, "empty", { value: "" });
}

export function useTableRows<K extends string, T = unknown>({
  searchedData,
  columnDefinitions,
  isLoading,
  loadingRowCount = 10,
}: UseTableRowsConfig<K, T>) {
  const loadingRow = useMemo(
    () => createLoadingRow(columnDefinitions),
    [columnDefinitions]
  );
  const emptyRow = useMemo(
    () => createEmptyRow(columnDefinitions),
    [columnDefinitions]
  );

  return useMemo(() => {
    if (searchedData.length > 0) {
      return searchedData;
    }

    return isLoading
      ? Array.from({ length: loadingRowCount }, (_, idx) => idx).map((idx) => ({
          ...loadingRow,
          key: `loading-${idx}`,
        }))
      : [emptyRow];
  }, [searchedData, isLoading, loadingRow, emptyRow, loadingRowCount]);
}
